import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  SimpleGrid,
  HStack,
  Divider,
  Text,
  Badge,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, AddIcon, SearchIcon } from "@chakra-ui/icons";
import CustomInput from "components/common/customInput/CustomInput";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { AppContext } from "AppProvider/AppContext";
import { appRequest } from "routes";

interface EstimateForm {
  currency: string;
  estimateRate: string;
  place: string;
}

const initialForm: EstimateForm = {
  currency: "",
  estimateRate: "",
  place: "",
};

const ForeignEstimate: React.FC = () => {
  const { toastNotify } = useContext(AppContext);
  const [list, setList] = useState<any[]>([]);
  const [form, setForm] = useState<EstimateForm>(initialForm);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchCurrency, setSearchCurrency] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleFetch = async () => {
    setFetching(true);
    try {
      const resp: any = await appRequest("foreignEstimate", "get", {});
      if (resp.status === "success") {
        setList(resp.data);
      }
    } catch (err: any) {
      toastNotify({
        title: "Error",
        message: err.message,
        type: "error",
      });
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    handleFetch();
  }, []);

  const handleChange = (name: keyof EstimateForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.currency || !form.estimateRate || !form.place) {
      toastNotify({
        title: "Validation Error",
        message: "All fields are required",
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        currency: form.currency.toUpperCase(),
        estimateRate: form.estimateRate,
        place: form.place,
      };

      if (editingId) {
        await appRequest("foreignEstimate", "update", { id: editingId, ...payload });
      } else {
        await appRequest("foreignEstimate", "create", payload);
      }

      toastNotify({
        title: "Success",
        message: editingId ? "Updated successfully" : "Saved successfully",
        type: "success",
      });
      setForm(initialForm);
      setEditingId(null);
      onClose();
      handleFetch();
    } catch (err: any) {
      toastNotify({
        title: "Operation Failed",
        message: err.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setLoading(true);
    try {
      await appRequest("foreignEstimate", "delete", { id: deletingId });
      toastNotify({
        title: "Success",
        message: "Deleted successfully",
        type: "success",
      });
      onDeleteClose();
      setDeletingId(null);
      handleFetch();
    } catch (err: any) {
      toastNotify({
        title: "Error",
        message: err.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredList = list.filter((item) =>
    item.currency.toLowerCase().includes(searchCurrency.toLowerCase())
  );

  return (
    <Box p={4}>
      <ShadowBox>
        <Flex mb={6} align="center" justify="space-between">
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="teal.600">
              Foreign Currency Estimates
            </Text>
            <Text fontSize="sm" color="gray.500">
              Manage exchange rates for international processes
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="teal"
            variant="solid"
            boxShadow="md"
            _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
            onClick={() => {
              setForm(initialForm);
              setEditingId(null);
              onOpen();
            }}
          >
            Add New Estimate
          </Button>
        </Flex>

        <Box mb={6} maxW="400px">
          <CustomInput
            name="searchCurrency"
            label="Filter by Currency"
            value={searchCurrency}
            onChange={(e: any) => setSearchCurrency(e.target.value)}
          />
        </Box>

        <Box shadow="sm" border="1px solid" borderColor="gray.100" borderRadius="xl" overflow="hidden">
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th color="gray.600" py={4}>Currency Code</Th>
                <Th color="gray.600">TT Buy Rate</Th>
                <Th color="gray.600">Currency Description</Th>
                <Th color="gray.600">Author</Th>
                <Th color="gray.600">Last Modified</Th>
                <Th color="gray.600" textAlign="center">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredList.length > 0 ? (
                filteredList.map((item) => (
                  <Tr key={item._id} _hover={{ bg: "gray.50" }} transition="0.2s">
                    <Td py={4}>
                      <Badge colorScheme="purple" fontSize="0.9em" px={3} borderRadius="full">
                        {item.currency}
                      </Badge>
                    </Td>
                    <Td fontWeight="600" color="teal.700">
                      {item.estimateRate}
                    </Td>
                    <Td color="gray.600">{item.place}</Td>
                    <Td fontSize="sm">
                      <Text fontWeight="medium">
                        {item.createdBy?.name || item.createdBy?.username || "Admin"}
                      </Text>
                    </Td>
                    <Td fontSize="xs" color="gray.500">
                      {new Date(item.updated_At).toLocaleString()}
                    </Td>
                    <Td textAlign="center">
                      <HStack spacing={2} justify="center">
                        <Tooltip label="Edit Record" hasArrow>
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => {
                              setForm({
                                currency: item.currency,
                                estimateRate: item.estimateRate.toString(),
                                place: item.place,
                              });
                              setEditingId(item._id);
                              onOpen();
                            }}
                          />
                        </Tooltip>
                        <Tooltip label="Delete Record" hasArrow>
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => {
                              setDeletingId(item._id);
                              onDeleteOpen();
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={10} color="gray.400">
                    No currency estimates found.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </ShadowBox>

      {/* CREATE/EDIT MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(8px)" />
        <ModalContent borderRadius="2xl" boxShadow="2xl" overflow="hidden">
          <ModalHeader bg="gray.10" py={6}>
            <Text fontSize="xl" fontWeight="700">
              {editingId ? "Modify Exchange Rate" : "New Exchange Rate"}
            </Text>
            <Text fontSize="xs" fontWeight="normal" color="gray.500" mt={1}>
              Please provide the current currency estimation details below.
            </Text>
          </ModalHeader>
          <Divider />
          <ModalBody py={8} px={8}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box>
                <CustomInput
                  name="currency"
                  label="Currency (e.g. USD)"
                  value={form.currency}
                  onChange={(e: any) => handleChange("currency", e.target.value)}
                />
              </Box>
              <Box>
                <CustomInput
                  name="estimateRate"
                  label="TT Buy Rate"
                  value={form.estimateRate}
                  onChange={(e: any) => handleChange("estimateRate", e.target.value)}
                />
              </Box>
              <Box gridColumn={{ md: "span 2" }}>
                <CustomInput
                  name="place"
                  label="Currency Description"
                  value={form.place}
                  onChange={(e: any) => handleChange("place", e.target.value)}
                />
              </Box>
            </SimpleGrid>
          </ModalBody>
          <Divider />
          <ModalFooter bg="gray.50" py={4} gap={3}>
            <Button variant="ghost" onClick={onClose} borderRadius="lg">
              Close
            </Button>
            <Button
              colorScheme="teal"
              px={8}
              borderRadius="lg"
              boxShadow="teal-subtle"
              onClick={handleSave}
              isLoading={loading}
              loadingText="Saving..."
            >
              {editingId ? "Save Changes" : "Create Record"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(3px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader fontSize="lg" fontWeight="bold">
            Confirm Deletion
          </ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600">
              Are you sure you want to delete this record? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              onClick={handleDelete}
              isLoading={loading}
              loadingText="Deleting..."
            >
              Confirm Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ForeignEstimate;
